<template>
  <a v-if='page && !featured' :href="'/' + uid" class='event-row compact-row'>
    <div class='event-header'>
      <h3 class='caret' v-html='page.title' />
      <h3 class='event-date'>{{ shortDate }}</h3>
    </div>
  </a>
  <div v-else-if='page' class='event-row'>
    <div class='event-header' @click='open = !open'>
      <h3 class='caret' :class='{ "is-open": open }' v-html='page.title' />
      <h3 class='event-date'>{{ shortDate }}</h3>
    </div>
    <transition name='expand'>
      <div class='event-body' v-show='open'>
        <div class='event-details'>
          <div class='event-actions'>
            <span class='event-tag' v-if='tag'>{{ tag }}</span>
            <a class='event-ical' :href='icalUrl' download>› Add to Calendar</a>
          </div>
          <div class='event-middle'>
            <p class='event-meta' v-if='location'>{{ location }}</p>
            <p class='event-meta' v-if='fullDate'>{{ fullDate }}</p>
          </div>
          <p class='event-intro' v-if='renderedIntro' v-html='renderedIntro' />
          <a class='learn-more' :href="'/' + uid">Learn more</a>
        </div>
        <figure class='event-image' v-if='imageUrl'>
          <Image :src='imageUrl' :srcset='imageSrcset' sizes="28vw" :alt='page.title' />
        </figure>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'
  import Image from './Image.vue'

  const kql = useStore();
  const props = defineProps(['uid', 'intro', 'featured', 'startOpen']);
  const open = ref(!!props.startOpen);
  const renderedIntro = computed(() => {
    const raw = props.intro || '';
    return raw.replace(/\*(.+?)\*/g, '<em>$1</em>');
  });

  const page = computed(() => props.uid ? pageFromPath(props.uid, kql.pages) : null);

  const tag = computed(() => {
    if (page.value?.pagetype === 'exhibition') return 'Exhibition';
    if (page.value?.pagetype === 'event') return 'Event';
    return '';
  });

  const location = computed(() => page.value?.location || page.value?.exhibitionlocation || '');

  const imageFile = computed(() =>
    page.value?.eventposter?.[0] ||
    page.value?.exhibitionposter?.[0] ||
    page.value?.coverimages?.[0] ||
    null
  );
  const imageUrl = computed(() => imageFile.value?.url || null);
  const imageSrcset = computed(() => imageFile.value?.srcset || null);

  const icalUrl = computed(() => {
    const dateStr = page.value?.eventdate || page.value?.startdate || '';
    return dateStr ? `/ics/${props.uid}.ics` : '#';
  });

const fmtDate = d => { const [y, m, day] = d.split('-'); return `${m}/${day}/${y}`; };

  const shortDate = computed(() => {
    const p = page.value;
    if (!p) return '';
    const isExhibition = p.pagetype === 'exhibition';
    const dateStr = p.eventdate || p.startdate || '';
    const endStr = isExhibition ? (p.enddate || '') : '';
    if (!dateStr) return p.appendix || '';
    return endStr ? `${fmtDate(dateStr)}–${fmtDate(endStr)}` : fmtDate(dateStr);
  });

  const fmtTime = t => {
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const mins = m !== '00' ? `:${m}` : '';
    return `${hour12}${mins}${ampm}`;
  };

  const fullDate = computed(() => {
    const p = page.value;
    if (!p) return '';

    if (p.pagetype === 'exhibition') {
      const startDate = new Date((p.startdate || '') + 'T12:00:00');
      if (!p.startdate) return '';
      const endDate = p.enddate ? new Date(p.enddate + 'T12:00:00') : null;
      const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      if (!endDate) return startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      return `${startStr}–${endStr}`;
    }

    const dateStr = p.eventdate || '';
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    const dayDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const timeStr = p.eventtime || '';
    if (!timeStr) return dayDate;
    const startTime = fmtTime(timeStr);
    const endTimeStr = p.eventendtime || '';
    if (!endTimeStr) return `${dayDate}, ${startTime}`;
    const endTime = fmtTime(endTimeStr);
    return `${dayDate}, ${startTime}–${endTime}`;
  });
</script>

<style scoped>
  .event-row {
    line-height: 1.2em;
    box-sizing: border-box;
    border-top: 1px solid var(--border);
    margin: 0;
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
    padding-bottom: 1rem;
    color: inherit;
    text-decoration: none;
  }

  .compact-row {
    opacity: 1 !important;
  }

  .compact-row .event-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
  }

  .compact-row:hover h3.caret::before { transform: translateX(0.1em); }

  h3 {
    padding-left: 0.75em;
    line-height: 1.1em;
    margin: 0;
    position: relative;
    font-size: 2.5rem;
  }

  h3.caret::before {
    position: absolute;
    left: 0;
    top: 0.35em;
    content: '\203a';
    display: inline-block;
    width: 0.5em;
    height: 0.5em;
    line-height: 0.15;
    text-align: center;
    transition: transform 0.2s ease;
    transform-origin: 50% 50%;
  }

  h3.caret.is-open::before {
    transform: rotate(90deg);
  }

  h3:not(.caret) { text-align: right; }

  .event-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 0.5rem;
    cursor: pointer;
  }

  .event-header:hover h3.caret:not(.is-open)::before { transform: translateX(0.1em); }

  .event-date { white-space: nowrap; flex-shrink: 0; padding-left: 0; }

  .event-meta-always {
    padding-left: 2.125rem;
    margin-top: 0.4rem;
    margin-bottom: 0.6rem;
  }

  .event-body {
    display: flex;
    gap: 2rem;
    align-items: stretch;
    padding-left: 2.125rem;
    padding-bottom: 3rem;
  }

  .event-details { flex: 1; display: flex; flex-direction: column; }

  .event-middle {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin-top: 0.25rem;
  }

  .event-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.4rem;
    margin-bottom: 0.6rem;
  }

  .event-tag {
    font-size: 0.9rem;
    font-family: "Maison Neue Demi", sans-serif;
    background: var(--second, #644646);
    color: rgba(255, 176, 5, 1);
    opacity: 0.6;
    border-radius: 2em;
    padding: 0.15em 0.8em;
    margin-left: -0.2em;
    letter-spacing: 0.02em;
    vertical-align: 0.1em;
  }

  .event-ical {
    font-size: 1rem;
    font-family: "Maison Neue Demi", sans-serif;
    letter-spacing: 0.01em;
    color: inherit;
    text-decoration: none;
    opacity: 0.7;
    line-height: 1.4em;
  }

  .event-ical:hover { opacity: 1; }

  .event-meta {
    margin: 0;
    font-size: 1rem;
    font-family: "Maison Neue Demi", sans-serif;
    opacity: 0.7;
    line-height: 1.4em;
    letter-spacing: 0.01em;
  }

  .event-intro {
    padding-top: 1.5rem;
    font-size: 1.5rem;
    line-height: 1.45em;
    max-width: 36em;
  }

  .learn-more {
    display: inline-block;
    align-self: flex-start;
    margin-top: 1.5rem;
    margin-left: -0.3em;
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.5rem;
    border-radius: 3em;
    padding: 0.55em 1.6em 0.6em;
    line-height: 1;
    color: inherit;
    background: rgba(235, 224, 225, 0.35);
    border: none;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .learn-more:hover {
    background: var(--first, rgba(74, 47, 48, 1));
    color: var(--light);
  }

  .event-image {
    flex: 0 0 auto;
    width: 28%;
    margin: 0;
    padding: 0;
    padding-top: 1.75rem;
  }

  .event-image img {
    width: 100%;
    display: block;
    max-height: 30rem;
    object-fit: contain;
    object-position: left top;
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: opacity 0.3s ease;
    overflow: hidden;
  }

  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
  }

  @media (max-width: 600px) {
    .event-body { flex-direction: column; padding-left: 1.5em; }
    .event-image { width: 100%; }
    .event-image img { max-height: none; }
    h3 { font-size: 2rem; }
    .event-tag { font-size: 1.2rem; }
    .event-ical { font-size: 1.3rem; }
    .event-meta { font-size: 1.3rem; }
    .event-intro { font-size: 1.6rem; }
    .compact-row h3 { font-size: 1.8rem; }
    .compact-row .event-date { font-size: 1.6rem; }
    .compact-row .event-header { padding-bottom: 0.5rem; }
  }
</style>
